import { useId, useMemo, useState } from 'react'
import type { PricePoint } from '@/lib/priceHistory'
import { formatCurrency, formatEthSpot } from '@/lib/format'

interface CurvePriceChartProps {
  points: PricePoint[]
  accent?: 'lime' | 'orange'
  height?: number
  compact?: boolean
  unit?: 'eth' | 'usd'
  showGraduation?: boolean
}

function formatPrice(value: number, unit: 'eth' | 'usd'): string {
  return unit === 'eth' ? formatEthSpot(value) : formatCurrency(value)
}

function toChartPoints(points: PricePoint[]): PricePoint[] {
  if (points.length === 0)
    return points

  const rest = points.filter((point, index) => {
    if (!Number.isFinite(point?.price) || point.price < 0)
      return false
    if (index === 0 && (point.supply <= 0 || point.price <= 0))
      return false
    if (index === 0 && point.supply === 0)
      return false
    return true
  })
  const origin: PricePoint = {
    t: points[0]?.t ?? 0,
    supply: 0,
    price: 0,
    phase: points[0]?.phase === 'pool' ? 'curve' : (points[0]?.phase ?? 'curve'),
  }
  return [origin, ...rest]
}

function monotoneChartPath(pts: { x: number, y: number }[]): string {
  if (pts.length === 0)
    return ''
  if (pts.length < 3)
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')

  const n = pts.length
  const dx: number[] = []
  const slope: number[] = []
  for (let i = 0; i < n - 1; i++) {
    const span = pts[i + 1].x - pts[i].x
    dx[i] = span
    slope[i] = span !== 0 ? (pts[i + 1].y - pts[i].y) / span : 0
  }

  const tangent = new Array<number>(n).fill(0)
  tangent[0] = slope[0] ?? 0
  tangent[n - 1] = slope[n - 2] ?? 0
  for (let i = 1; i < n - 1; i++) {
    if ((slope[i - 1] ?? 0) * (slope[i] ?? 0) <= 0)
      tangent[i] = 0
    else
      tangent[i] = ((slope[i - 1] ?? 0) + (slope[i] ?? 0)) / 2
  }

  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(slope[i] ?? 0) < 1e-12) {
      tangent[i] = 0
      tangent[i + 1] = 0
      continue
    }
    const a = tangent[i] / slope[i]
    const b = tangent[i + 1] / slope[i]
    const s = a * a + b * b
    if (s > 9) {
      const t = 3 / Math.sqrt(s)
      tangent[i] = t * a * slope[i]
      tangent[i + 1] = t * b * slope[i]
    }
  }

  let d = `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i]
    if (!h)
      continue
    const p0 = pts[i]
    const p1 = pts[i + 1]
    const cp1x = p0.x + h / 3
    const cp1y = p0.y + tangent[i] * h / 3
    const cp2x = p1.x - h / 3
    const cp2y = p1.y - tangent[i + 1] * h / 3
    d += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p1.x.toFixed(2)},${p1.y.toFixed(2)}`
  }
  return d
}

/**
 * Lightweight dependency-free SVG price chart, styled after live pump.fun-style
 * bonding curve charts: a filled area line ending at the current price with a
 * pulsing "live" marker and hover tooltip.
 */
function CurvePriceChart({
  points,
  accent = 'lime',
  height = 160,
  compact = false,
  unit = 'usd',
  showGraduation = false,
}: CurvePriceChartProps) {
  const gradientId = useId()
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const width = 100
  const strokeColor = accent === 'lime' ? '#a3e635' : '#fb923c'
  const fillFrom = accent === 'lime' ? 'rgba(163,230,53,0.35)' : 'rgba(251,146,60,0.35)'

  const series = useMemo(() => toChartPoints(points), [points])

  const { pathD, areaD, coords, minPrice, maxPrice, graduationX } = useMemo(() => {
    if (series.length === 0) {
      return {
        pathD: '',
        areaD: '',
        coords: [] as { x: number, y: number }[],
        minPrice: 0,
        maxPrice: 0,
        graduationX: null as number | null,
      }
    }

    const prices = series.map(p => p.price)
    const min = 0
    const max = Math.max(0, ...prices)
    const range = max - min || 1

    const maxSupply = Math.max(0, ...series.map(p => p.supply))
    const pts = series.map((p) => {
      const x = maxSupply > 0 ? (p.supply / maxSupply) * width : 0
      const y = height - ((p.price - min) / range) * (height - 12) - 6
      return { x, y }
    })

    const line = monotoneChartPath(pts)
    const last = pts[pts.length - 1]
    const area = last
      ? `${line} L${last.x.toFixed(2)},${height} L0,${height} Z`
      : `${line} L${width},${height} L0,${height} Z`
    const graduationIndex = series.findIndex(p => p.phase === 'pool')
    const nextGraduationX = graduationIndex > 0 ? pts[graduationIndex]?.x ?? null : null

    return { pathD: line, areaD: area, coords: pts, minPrice: min, maxPrice: max, graduationX: nextGraduationX }
  }, [series, height])

  if (series.length === 0)
    return null

  const lastPoint = series[series.length - 1]
  const active = hoverIndex !== null ? series[hoverIndex] : lastPoint
  const activeCoord = hoverIndex !== null ? coords[hoverIndex] : coords[coords.length - 1]
  const liveLabel = lastPoint?.phase === 'pool' ? 'Live pool chart' : 'Live curve chart'
  const phaseLabel = active?.phase === 'pool' ? 'Pool' : active?.phase === 'curve' ? 'On curve' : null
  const currentPriceLabel = formatPrice(lastPoint.price, unit)

  return (
    <div className="relative">
      <div className="mb-2 flex items-center justify-between">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${accent === 'lime' ? 'text-lime-400' : 'text-orange-400'}`}>
          <span className="relative flex h-2 w-2">
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${accent === 'lime' ? 'bg-lime-400' : 'bg-orange-400'}`} />
            <span className={`relative inline-flex h-2 w-2 rounded-full ${accent === 'lime' ? 'bg-lime-400' : 'bg-orange-400'}`} />
          </span>
          {liveLabel}
        </span>
        {!compact && (
          <span className="text-xs text-zinc-500">
            {formatPrice(minPrice, unit)}
            {' '}
            &ndash;
            {' '}
            {formatPrice(maxPrice, unit)}
          </span>
        )}
      </div>

      <div
        className="relative w-full"
        style={{ height }}
        role="img"
        aria-label={`${liveLabel}, current price ${currentPriceLabel} per token`}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="h-full w-full overflow-visible"
          onMouseLeave={() => setHoverIndex(null)}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={fillFrom} />
              <stop offset="100%" stopColor="rgba(0,0,0,0)" />
            </linearGradient>
          </defs>
          <path d={areaD} fill={`url(#${gradientId})`} stroke="none" />
          <path d={pathD} fill="none" stroke={strokeColor} strokeWidth={compact ? '1.4' : '2'} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />

          {showGraduation && graduationX !== null
            ? (
                <line
                  x1={graduationX}
                  x2={graduationX}
                  y1="0"
                  y2={height}
                  stroke={strokeColor}
                  strokeOpacity="0.55"
                  strokeWidth="1"
                  strokeDasharray="2 2"
                  vectorEffect="non-scaling-stroke"
                />
              )
            : null}

          {activeCoord && (
            <>
              <line
                x1={activeCoord.x}
                x2={activeCoord.x}
                y1="0"
                y2={height}
                stroke={strokeColor}
                strokeOpacity="0.25"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <circle cx={activeCoord.x} cy={activeCoord.y} r="2.2" fill={strokeColor} />
            </>
          )}

          {coords.map((c, i) => (
            <rect
              key={i}
              x={Math.max(0, c.x - width / series.length / 2)}
              y={0}
              width={width / series.length}
              height={height}
              fill="transparent"
              onMouseEnter={() => setHoverIndex(i)}
              className="cursor-crosshair"
            />
          ))}
        </svg>

        {showGraduation && graduationX !== null && !compact
          ? (
              <span
                className="pointer-events-none absolute top-0 rounded-full border border-lime-400/30 bg-black/80 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-lime-400"
                style={{ left: `${Math.min(72, Math.max(0, graduationX))}%` }}
              >
                Grad
              </span>
            )
          : null}

        {active && (
          <div
            className="pointer-events-none absolute -top-1 rounded-md border border-zinc-800 bg-black px-2 py-1 text-[11px] font-medium text-white shadow-lg"
            style={{
              left: `${Math.min(78, Math.max(0, activeCoord?.x ?? 0))}%`,
            }}
          >
            {formatPrice(active.price, unit)}
            {phaseLabel
              ? (
                  <span className="ml-1 text-[10px] font-normal text-zinc-400">
                    {phaseLabel}
                  </span>
                )
              : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default CurvePriceChart
